import {
    Component,
    lazy,
    Suspense,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Chip,
    LinearProgress,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTheme } from '@mui/material/styles';
import { useEventMarkets } from '../api/hooks';
import { useEventAlerts } from '../api/useAlerts';
import { useMultiMarketProbability } from '../api/useMultiMarketProbability';
import { NO_MIN_TARGET, keepGroupHeight } from '../../../root/touchTheme';
import { SectionCard } from '../components/shared';
import { PageFrame } from '../../../components/common/page/PageFrame';
import { canUseFeature } from '../../../auth/canUseFeature';
import { useAccessFeatures } from '../../../auth/useAccessFeatures';
import { assignMarketColors } from '../utils/marketColors';
import { ChartStack, ChartStackPlaceholder } from './chart/ChartStack';
import { rangeLabelOf, seriesChange } from './chart/changeOverRange';
import { ChartToolbar, ChartToolbarPlaceholder } from './chart/ChartToolbar';
import { fullscreenCardSx, fullscreenChartHeight } from './chart/fullscreen';
import { useCompactChart } from './chart/useCompactChart';
import { useFullscreenFill } from './chart/useFullscreenFill';
import { alertSeries } from './chart/series/alerts';
import { flowSeries } from './chart/series/flow';
import {
    probabilitySeries,
    type ProbabilityMode,
} from './chart/series/probability';
import { panelTitle } from './chart/format';
import { groupOnMain, moveGroup, planPanels } from './chart/placement';
import { positionSeries, type PositionMode } from './chart/series/positions';
import { tradeMarkerSeries } from './chart/series/trades';
import { volumeSeries } from './chart/series/volume';
import type { ChartSeries, PanelId, SeriesMoves } from './chart/seriesTypes';
import type { ClockMarket, ClockOutput } from './clock/types';
import { ExportBox } from './export/ExportBox';
import { loadCandlePage, OldFunctionError } from './export/exportClient';
import type { ExportMarket } from './export/exportTypes';
import { FLOW_GROUP_IDS, FLOW_GROUPS, type FlowGroupId } from './flow/groups';
import { anyFlow, flowRank } from './flow/summary';
import { useFlowSeries, type FlowScope } from './flow/useFlowSeries';
import { useLatestFlow } from './flow/useLatestFlow';
import { EventHeader } from './header/EventHeader';
import { OutcomeStrip } from './header/OutcomeStrip';
import { eventCan, quoteWith } from './model/capabilities';
import { eventFromPolymarket } from './model/fromPolymarket';
import type { Outcome } from './model/types';
import { OutcomeList } from './outcomes/OutcomeList';
import {
    MAX_SHOWN_ACTIVE,
    probabilityOf,
    splitOutcomes,
    volumeOf,
} from './outcomes/ranking';
import { useOutcomeSort } from './outcomes/useOutcomeSort';
import { useEventSelection } from './useEventSelection';
import { useEventWindow } from './useEventWindow';
import { useLiveEvent } from './useLiveEvent';
import { isQuickRange, lifetimeOf, QUICK_RANGES, quickRange } from './lifetime';
import { walletColors } from './wallets/colors';
import {
    DEFAULT_TRADE_TYPES,
    type TradeTypeId,
    type WalletTrade,
} from './wallets/tradeTypes';
import { useWalletPositions } from './wallets/useWalletPositions';
import { useWalletTrades } from './wallets/useWalletTrades';
import { WalletsPanel } from './wallets/WalletsPanel';

const CHART_HEIGHT_SHARE = 0.55;
// Below 900 px the strip and the toolbar sit above the chart (the mockup: a
// 340 px plot and its axis on an 844 px screen).
const CHART_HEIGHT_SHARE_COMPACT = 0.45;
const MIN_CHART_HEIGHT = 320;
// The page draws every card at its final size from the first frame and fills
// each one once, when everything in it has arrived (see the loading spec).
// The outcome list waits this long for the flow of its outcomes; a later
// answer adds the tags after.
const FLOW_WAIT_MS = 3000;
// The chart fills once the prices of all its outcomes are in, or this long
// after the first of them; later lines join a chart already drawn.
const PRICES_WAIT_MS = 3000;
// The values strip above a phone's chart before the event is known: the six
// outcomes the chart shows and "Others".
const DEFAULT_VALUE_ROWS = MAX_SHOWN_ACTIVE + 1;
// The clock's header row above its panel, until the real one is there to be
// measured: one row of controls on a wide screen, several on a narrow one.
const CLOCK_STRIP_HEIGHT = { wide: 32, compact: 173 };
const NO_STRIP_HEIGHTS: Readonly<Record<string, number>> = {};
const NO_OUTCOMES: Outcome[] = [];
const NO_MOVES: SeriesMoves = {};
const NO_IDS: readonly string[] = [];
const NO_WALLETS: string[] = [];
const NO_HIDDEN: ReadonlySet<PanelId> = new Set();
// The panels that fold away in a full-screen chart without the room for them.
const FOLDABLE: ReadonlySet<PanelId> = new Set<PanelId>(['clock']);
const PANEL_TOGGLE_SX = {
    ...keepGroupHeight(18),
    flexShrink: 0,
    '& .MuiToggleButton-root': {
        ...NO_MIN_TARGET,
        py: 0,
        px: 0.75,
        fontSize: '10px',
        fontWeight: 600,
        textTransform: 'none',
        borderColor: 'divider',
    },
} as const;

// The event clock: researchers only. React.lazy loads the chunk on first
// render, so other users never fetch any of it (checked by
// scripts/clock-checks/check-build.mjs).
const ClockBridge = lazy(() => import('./clock/ClockBridge'));

// If the clock chunk cannot load (a stale tab after a deploy), the event view
// carries on without the clock.
class ClockBoundary extends Component<
    { children: ReactNode; onFailed?: () => void },
    { failed: boolean }
> {
    state = { failed: false };

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error: unknown) {
        console.error('event clock could not be loaded:', error);
        this.props.onFailed?.();
    }

    render() {
        return this.state.failed ? null : this.props.children;
    }
}

function isOldFunction(error: unknown): boolean {
    return error instanceof OldFunctionError;
}

function useWindowHeight(): number {
    const [height, setHeight] = useState(() => window.innerHeight);
    useEffect(() => {
        const onResize = () => setHeight(window.innerHeight);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return height;
}

// True once `waiting` has held for `ms` for this `key` (a new key starts over).
function useWaitOver(
    key: string | null,
    waiting: boolean,
    ms: number
): boolean {
    const [overFor, setOverFor] = useState<string | null>(null);
    useEffect(() => {
        // An earlier wait for the same key (left and come back to) is over.
        setOverFor(null);
        if (key === null || !waiting) return;
        const timer = setTimeout(() => setOverFor(key), ms);
        return () => clearTimeout(timer);
    }, [key, waiting, ms]);
    return key !== null && overFor === key;
}

function marketIdOf(outcome: Outcome): string | null {
    return quoteWith(outcome, 'probability')?.market.marketId ?? null;
}

export function EventView() {
    const theme = useTheme();
    const compact = useCompactChart();
    const fill = useFullscreenFill();
    const { eventId } = useParams<{ eventId: string }>();
    const {
        data: response,
        loading: eventLoading,
        error: eventError,
    } = useEventMarkets(eventId ?? null);
    const fetchedEvent = useMemo(
        () => (response ? eventFromPolymarket(response) : null),
        [response]
    );
    const event = useLiveEvent(fetchedEvent);

    const navigate = useNavigate();
    const { search } = useLocation();
    const slug = event?.slug ?? null;
    useEffect(() => {
        if (slug && eventId && eventId !== slug) {
            navigate(`/markets/event/${slug}${search}`, { replace: true });
        }
    }, [slug, eventId, search, navigate]);

    const selection = useEventSelection(event);
    const [sort, setSort] = useOutcomeSort();

    const canFlow = event ? eventCan(event, 'flow') : false;
    const canWallets = event ? eventCan(event, 'wallets') : false;
    const latest = useLatestFlow(canFlow && event ? event.id : null);
    // What the latest flow says is known once it has answered, or once it has
    // kept the page waiting long enough. The outcome list fills in one step
    // then, its rows and their flow together.
    const flowWaitOver = useWaitOver(
        canFlow ? event?.id ?? null : null,
        !latest.settled,
        FLOW_WAIT_MS
    );
    const latestKnown = !canFlow || latest.settled || flowWaitOver;
    const listReady = event !== null && latestKnown;
    const allMarketIds = useMemo(
        () =>
            event
                ? event.outcomes
                      .map(marketIdOf)
                      .filter((id): id is string => id !== null)
                : [],
        [event]
    );
    const showFlow = canFlow && anyFlow(latest.byMarket, allMarketIds);
    const effectiveSort = sort === 'flow' && !showFlow ? 'probability' : sort;
    const rank = useCallback(
        (outcome: Outcome) => flowRank(latest.byMarket, marketIdOf(outcome)),
        [latest.byMarket]
    );

    const split = useMemo(
        () =>
            event
                ? splitOutcomes(event.outcomes, effectiveSort, rank)
                : { shown: [], hidden: [] },
        [event, effectiveSort, rank]
    );

    const hasSelection =
        selection.selectedOutcomes.length > 0 &&
        !selection.isSingleOutcomeEvent;
    // Sorted by flow, the outcomes the chart shows are known once the flow is:
    // until then it asks for no prices, so it fills once, with the right ones.
    const waitingForFlowOrder =
        sort === 'flow' &&
        canFlow &&
        !latestKnown &&
        !hasSelection &&
        !selection.isSingleOutcomeEvent;
    const chartOutcomes = hasSelection
        ? selection.selectedOutcomes
        : selection.isSingleOutcomeEvent
          ? selection.selectedOutcomes
          : waitingForFlowOrder
            ? NO_OUTCOMES
            : split.shown;

    const colors = useMemo(() => {
        const byProbability = [...split.shown].sort(
            (a, b) => (probabilityOf(b) ?? 0) - (probabilityOf(a) ?? 0)
        );
        const shownIds = new Set(byProbability.map(o => o.id));
        const extra = selection.selectedOutcomes.filter(
            o => !shownIds.has(o.id)
        );
        return assignMarketColors([...byProbability, ...extra].map(o => o.id));
    }, [split.shown, selection.selectedOutcomes]);

    const chartColors = useMemo(() => {
        const map = new Map<string, string>();
        for (const o of chartOutcomes) {
            const color = colors.get(o.id);
            if (color) map.set(o.id, color);
        }
        return map;
    }, [chartOutcomes, colors]);

    const marketIds = useMemo(
        () =>
            chartOutcomes
                .map(marketIdOf)
                .filter((id): id is string => id !== null),
        [chartOutcomes]
    );

    const access = useAccessFeatures();
    const researcher = canUseFeature(access, 'researcher');
    const [clock, setClock] = useState<ClockOutput | null>(null);
    const [clockFailed, setClockFailed] = useState(false);
    const onClockFailed = useCallback(() => setClockFailed(true), []);
    const wasResearcher = useRef(false);
    useEffect(() => {
        if (researcher) {
            wasResearcher.current = true;
            return;
        }
        setClock(null);
        if (wasResearcher.current) {
            // The feature was taken away mid-session: stop the engine. The
            // module is already loaded, so this fetches nothing.
            wasResearcher.current = false;
            void import('./clock/ClockBridge').then(m => m.dropClockEngine());
        }
    }, [researcher]);
    const clockMarkets = useMemo<ClockMarket[]>(
        () =>
            chartOutcomes.flatMap(outcome => {
                const id = marketIdOf(outcome);
                return id
                    ? [
                          {
                              id,
                              label: outcome.label,
                              color:
                                  chartColors.get(outcome.id) ??
                                  theme.palette.text.secondary,
                          },
                      ]
                    : [];
            }),
        [chartOutcomes, chartColors, theme.palette.text.secondary]
    );

    const win = useEventWindow(fetchedEvent, marketIds.length);
    const { bounds, range, setRange, fetchRange, resolution } = win;

    const {
        series: candles,
        loading: candlesLoading,
        allLoaded: candlesAllLoaded,
        settled: candlesSettled,
    } = useMultiMarketProbability(
        marketIds,
        fetchRange?.[0] ?? null,
        fetchRange?.[1] ?? null,
        resolution
    );

    const { alerts, settled: alertsSettled } = useEventAlerts(event?.id);

    const single = selection.single;
    const singleMarketId = single
        ? quoteWith(single, 'flow')?.market.marketId ?? null
        : null;
    const flowScope = useMemo<FlowScope | null>(
        () =>
            canFlow && singleMarketId
                ? { kind: 'outcome', marketId: singleMarketId }
                : null,
        [canFlow, singleMarketId]
    );
    const flow = useFlowSeries(flowScope, resolution);
    const [flowGroups, setFlowGroups] =
        useState<readonly FlowGroupId[]>(FLOW_GROUP_IDS);
    const allFlowSeries = useMemo(
        () => flowSeries(flow.byGroup),
        [flow.byGroup]
    );
    const shownFlowSeries = useMemo(() => {
        const shown = allFlowSeries.filter(s =>
            flowGroups.some(id => s.id === `flow:${id}`)
        );
        return shown.some(s => s.hasData) ? shown : allFlowSeries;
    }, [allFlowSeries, flowGroups]);
    const flowChoice = allFlowSeries.filter(s => s.hasData).length > 1;

    const wallets = useMemo(
        () => (singleMarketId && canWallets ? selection.wallets : NO_WALLETS),
        [singleMarketId, canWallets, selection.wallets]
    );
    const walletTradesStart = useMemo(() => {
        const starts = [
            event?.startDate ?? null,
            ...(single?.quotes.map(q => q.startDate) ?? []),
        ].filter((d): d is Date => d !== null);
        return starts.length > 0
            ? new Date(Math.min(...starts.map(d => d.getTime())))
            : null;
    }, [event, single]);
    const walletTradesEnd = useMemo(() => {
        if (!single || single.quotes.some(q => !q.closed)) return null;
        const ends = [
            event?.endDate ?? null,
            ...single.quotes.map(q => q.endDate),
        ].filter((d): d is Date => d !== null);
        return ends.length > 0
            ? new Date(Math.max(...ends.map(d => d.getTime())))
            : null;
    }, [event, single]);
    const [tradeTypes, setTradeTypes] =
        useState<readonly TradeTypeId[]>(DEFAULT_TRADE_TYPES);
    const [positionMode, setPositionMode] = useState<PositionMode>('sides');
    const walletTrades = useWalletTrades(
        event?.id ?? null,
        singleMarketId,
        wallets,
        walletTradesStart,
        walletTradesEnd,
        tradeTypes
    );
    const walletPositions = useWalletPositions(
        singleMarketId,
        wallets,
        walletTradesStart,
        walletTradesEnd
    );
    const failedWallets = useMemo(
        () =>
            new Set([
                ...walletTrades.failedWallets,
                ...walletPositions.failedWallets,
            ]),
        [walletTrades.failedWallets, walletPositions.failedWallets]
    );
    const retryTrades = walletTrades.retryWallet;
    const retryPositions = walletPositions.retryWallet;
    const retryWallet = useCallback(
        (wallet: string) => {
            retryTrades(wallet);
            retryPositions(wallet);
        },
        [retryTrades, retryPositions]
    );
    const fillsByWallet = useMemo(() => {
        const map = new Map<string, WalletTrade[]>();
        for (const [wallet, trades] of walletTrades.tradesByWallet) {
            map.set(
                wallet,
                trades.filter(t => t.tradeType === 'fill')
            );
        }
        return map;
    }, [walletTrades.tradesByWallet]);
    const walletColorMap = useMemo(() => walletColors(wallets), [wallets]);

    const viewClosed = single
        ? single.quotes.every(q => q.closed)
        : event?.status === 'closed';
    const boundsEndMs = bounds?.[1].getTime() ?? null;
    const lifetime = useMemo(() => {
        if (!event || boundsEndMs === null) return null;
        const quoteDates = (pick: 'startDate' | 'endDate') =>
            (single?.quotes ?? [])
                .map(q => q[pick])
                .filter((d): d is Date => d !== null)
                .map(d => d.getTime());
        const starts = quoteDates('startDate');
        const ends = quoteDates('endDate');
        const start =
            starts.length > 0 ? new Date(Math.min(...starts)) : event.startDate;
        const end =
            ends.length > 0 ? new Date(Math.max(...ends)) : event.endDate;
        return lifetimeOf(
            start,
            end,
            viewClosed,
            viewClosed ? boundsEndMs : Date.now()
        );
    }, [event, single, viewClosed, boundsEndMs]);

    const [lastCandleMs, setLastCandleMs] = useState<number | null>(null);
    const marketsKey = marketIds.join(',');
    useEffect(() => {
        setLastCandleMs(null);
    }, [marketsKey]);
    useEffect(() => {
        if (range !== null) return;
        let last: number | null = null;
        for (const s of candles) {
            const t = s.data[s.data.length - 1]?.t.getTime();
            if (t !== undefined && (last === null || t > last)) last = t;
        }
        if (last !== null) setLastCandleMs(last);
    }, [candles, range]);
    const anchorMs =
        viewClosed && lastCandleMs !== null ? lastCandleMs : boundsEndMs;
    const quickRanges = useMemo(() => {
        const list = QUICK_RANGES.map(q => ({
            id: q.id,
            label: q.label,
            disabled:
                !bounds ||
                anchorMs === null ||
                quickRange(q.spanMs, bounds, anchorMs) === null,
            active:
                anchorMs !== null && isQuickRange(range, q.spanMs, anchorMs),
        }));
        return [
            ...list,
            {
                id: 'all',
                label: 'All',
                disabled: false,
                active: range === null,
            },
        ];
    }, [bounds, anchorMs, range]);
    const onQuickRange = useCallback(
        (id: string) => {
            const choice = QUICK_RANGES.find(q => q.id === id);
            if (!choice || !bounds || anchorMs === null) {
                setRange(null);
                return;
            }
            setRange(quickRange(choice.spanMs, bounds, anchorMs));
        },
        [bounds, anchorMs, setRange]
    );
    const shownRange = range ?? bounds;
    const rangeLabel = rangeLabelOf(
        quickRanges.find(q => q.active)?.label,
        shownRange?.[0].getTime() ?? 0,
        shownRange?.[1].getTime() ?? 0
    );

    const [chosenMode, setChosenMode] = useState<ProbabilityMode | null>(null);
    const eventKey = event?.id ?? null;
    useEffect(() => {
        setChosenMode(null);
    }, [eventKey]);
    const [showVolume, setShowVolume] = useState(false);
    const [moves, setMoves] = useState<SeriesMoves>(NO_MOVES);
    const [fullscreen, setFullscreen] = useState(false);
    const [hiddenPanels, setHiddenPanels] =
        useState<ReadonlySet<PanelId>>(NO_HIDDEN);
    // In full screen on a small or touch screen a panel (the clock) folds to
    // its chip when the chart would not keep its floor beside it. `folded`
    // is what the chart reports; `opened` the foldable panels the user has
    // opened since this full screen began (they stay open, the card scrolls).
    // Showing a panel or sending the clock lines back also fills it on the
    // page, so it is emptied when a full screen begins, not when one ends.
    const [folded, setFolded] = useState<ReadonlySet<PanelId>>(NO_HIDDEN);
    const [opened, setOpened] = useState<ReadonlySet<PanelId>>(NO_HIDDEN);
    const onFolded = useCallback(
        (next: ReadonlySet<PanelId>) =>
            setFolded(prev =>
                prev.size === next.size && [...next].every(id => prev.has(id))
                    ? prev
                    : next
            ),
        []
    );
    const onToggleFullscreen = useCallback(() => {
        if (!fullscreen) setOpened(NO_HIDDEN);
        setFullscreen(!fullscreen);
    }, [fullscreen]);
    const foldPanels = useMemo(
        () =>
            fullscreen && fill
                ? new Set([...FOLDABLE].filter(id => !opened.has(id)))
                : NO_HIDDEN,
        [fullscreen, fill, opened]
    );
    const candlesAllowed = chartOutcomes.length === 1;
    const stackedAllowed =
        chartOutcomes.length === 1 ||
        (event !== null && event.kind !== 'independent');
    const exclusive =
        event !== null &&
        (event.kind === 'categories' || event.kind === 'ordered');
    const mode: ProbabilityMode =
        chosenMode ??
        (exclusive || event?.kind === 'binary' ? 'stacked' : 'line');
    const chartMode: ProbabilityMode =
        (mode === 'candles' && !candlesAllowed) ||
        (mode === 'stacked' && !stackedAllowed)
            ? 'line'
            : mode;
    const stacked = chartMode === 'stacked';

    useEffect(() => {
        if (!fullscreen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setFullscreen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [fullscreen]);

    const windowHeight = useWindowHeight();
    const fit = fullscreenChartHeight(windowHeight, fill);
    const availableHeight = fullscreen
        ? fit.height
        : Math.max(
              MIN_CHART_HEIGHT,
              Math.round(
                  windowHeight *
                      (compact
                          ? CHART_HEIGHT_SHARE_COMPACT
                          : CHART_HEIGHT_SHARE)
              )
          );

    const series = useMemo<ChartSeries[]>(() => {
        if (!event || !bounds) return [];
        const list = probabilitySeries(
            chartOutcomes,
            candles,
            chartColors,
            chartMode,
            false,
            theme.palette.text.secondary,
            exclusive && chartOutcomes.length === 1 ? 'Others' : null,
            Math.min(Date.now(), bounds[1].getTime())
        );
        if (showVolume) {
            list.push(volumeSeries(candles, theme.palette.text.primary));
        }
        list.push(
            alertSeries(
                alerts,
                bounds,
                new Set(marketIds),
                stacked && chartOutcomes.length > 1
            )
        );
        if (!stacked || chartOutcomes.length === 1) {
            list.push(...tradeMarkerSeries(fillsByWallet, walletColorMap));
        }
        list.push(...shownFlowSeries);
        list.push(
            ...positionSeries(
                walletPositions.positionsByWallet,
                walletColorMap,
                positionMode
            )
        );
        return list;
    }, [
        event,
        bounds,
        chartOutcomes,
        candles,
        chartColors,
        chartMode,
        stacked,
        showVolume,
        alerts,
        marketIds,
        fillsByWallet,
        walletPositions.positionsByWallet,
        positionMode,
        walletColorMap,
        shownFlowSeries,
        theme.palette.text.primary,
        theme.palette.text.secondary,
        exclusive,
    ]);
    const singleChange = useMemo(() => {
        if (!single || !shownRange) return null;
        const s = series.find(x => x.id.startsWith(`prob:${single.id}:`));
        return s
            ? seriesChange(
                  s,
                  shownRange[0].getTime(),
                  shownRange[1].getTime(),
                  // the number the header shows, so the change adds up to it
                  quoteWith(single, 'probability')?.probability ?? undefined
              )
            : null;
    }, [single, shownRange, series]);

    // The clock lines join the list here, so a clock update does not rebuild
    // every other series above.
    const clockSeries = clock?.series ?? null;
    const chartSeries = useMemo(
        () => (clockSeries ? [...series, ...clockSeries] : series),
        [series, clockSeries]
    );

    const plan = useMemo(
        () => planPanels(chartSeries, moves),
        [chartSeries, moves]
    );
    const hiddenChips = useMemo(
        () =>
            plan
                .filter(
                    p =>
                        p.id !== 'probability' &&
                        (hiddenPanels.has(p.id) || folded.has(p.id))
                )
                .map(p => ({
                    id: p.id,
                    title: panelTitle(p.id, p.series[0]?.label ?? ''),
                })),
        [plan, hiddenPanels, folded]
    );

    // The clock lines can sit on the main chart. "On main" is a mode: lines
    // that show up later (another outcome, a pinned run) join the others,
    // until the user turns it off or the last line has gone home.
    const clockIdsKey = (clockSeries ?? [])
        .filter(s => s.home === 'clock')
        .map(s => s.id)
        .join('\n');
    const clockIds = useMemo(
        () => (clockIdsKey === '' ? NO_IDS : clockIdsKey.split('\n')),
        [clockIdsKey]
    );
    const clockIdsRef = useRef(clockIds);
    clockIdsRef.current = clockIds;
    const [clockFollow, setClockFollow] = useState(false);
    const followedClockIds = useRef<ReadonlySet<string>>(new Set());
    const onClockOnMain = useCallback((on: boolean) => {
        const shown = clockIdsRef.current;
        if (on) {
            // Merged, not replaced: a line that is not drawn now (a cleared
            // pinned run) keeps its place in the list, so it is forgotten when
            // the mode ends.
            followedClockIds.current = new Set([
                ...followedClockIds.current,
                ...shown,
            ]);
            setMoves(prev => moveGroup(prev, shown, 'probability'));
        } else {
            // Also lines that are not drawn now, so they do not come back on
            // the main chart later.
            const all = [...new Set([...shown, ...followedClockIds.current])];
            followedClockIds.current = new Set();
            setMoves(prev => moveGroup(prev, all, 'home'));
            // The lines go back to the clock panel: it must be there to show
            // them (and the header), also if it was hidden or folded.
            setOpened(prev => new Set(prev).add('clock'));
            setHiddenPanels(prev => {
                if (!prev.has('clock')) return prev;
                const next = new Set(prev);
                next.delete('clock');
                return next;
            });
        }
        setClockFollow(on);
    }, []);
    // Before paint, so a new line is never seen in the clock panel for a frame.
    useLayoutEffect(() => {
        if (!clockFollow) return;
        const fresh = clockIds.filter(id => !followedClockIds.current.has(id));
        if (fresh.length === 0) return;
        followedClockIds.current = new Set([
            ...followedClockIds.current,
            ...fresh,
        ]);
        setMoves(prev => moveGroup(prev, fresh, 'probability'));
    }, [clockFollow, clockIds]);
    const handleMoves = useCallback(
        (next: SeriesMoves) => {
            const onMain = (m: SeriesMoves) =>
                clockIds.some(id => m[id] === 'probability');
            if (clockFollow && onMain(moves) && !onMain(next)) {
                // This move took the last clock line off the main chart: the
                // mode ends. (A move of another series changes nothing here.)
                // Lines that are not drawn now would otherwise come back on
                // the main chart.
                const gone = [...followedClockIds.current].filter(
                    id => !clockIds.includes(id)
                );
                followedClockIds.current = new Set();
                setClockFollow(false);
                setMoves(moveGroup(next, gone, 'home'));
                return;
            }
            setMoves(next);
        },
        [clockFollow, clockIds, moves]
    );
    const clockMain = useMemo(
        () => groupOnMain(chartSeries, moves, clockIds),
        [chartSeries, moves, clockIds]
    );
    // Where the clock is drawn: its own panel, or other panels. Its header
    // stays on screen either way.
    const clockPanelShown = plan.some(p => p.id === 'clock');
    const clockOnMainPanel = plan.some(
        p => p.id === 'probability' && p.series.some(s => s.home === 'clock')
    );
    const clockOnScreenElsewhere = plan.some(
        p =>
            p.id !== 'clock' &&
            (p.id === 'probability' || !hiddenPanels.has(p.id)) &&
            p.series.some(s => s.home === 'clock')
    );
    const hidePanel = useCallback(
        (panel: PanelId) => setHiddenPanels(prev => new Set(prev).add(panel)),
        []
    );
    const showPanel = useCallback((panel: PanelId) => {
        // A chip of a folded panel opens it for the rest of this full screen.
        setOpened(prev => new Set(prev).add(panel));
        setHiddenPanels(prev => {
            const next = new Set(prev);
            next.delete(panel);
            return next;
        });
    }, []);
    const clockHeader = clock?.header ?? null;
    // Its own panel is gone, hidden or folded, and a clock line is on the
    // main chart.
    const clockAway = hiddenPanels.has('clock') || folded.has('clock');
    const clockHeaderOnMain =
        (!clockPanelShown || clockAway) && clockOnMainPanel;
    const panelExtras = useMemo(
        () => ({
            position: (
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={positionMode}
                    onChange={(_, value: PositionMode | null) => {
                        if (value) setPositionMode(value);
                    }}
                    sx={PANEL_TOGGLE_SX}
                >
                    <ToggleButton value="sides">YES / NO</ToggleButton>
                    <ToggleButton value="net">Net</ToggleButton>
                </ToggleButtonGroup>
            ),
            flow: flowChoice ? (
                <ToggleButtonGroup
                    size="small"
                    value={flowGroups}
                    aria-label="Flow groups on the chart"
                    onChange={(_, value: FlowGroupId[]) => {
                        if (value.length > 0) setFlowGroups(value);
                    }}
                    sx={PANEL_TOGGLE_SX}
                >
                    {FLOW_GROUPS.map(group => (
                        <ToggleButton key={group.id} value={group.id}>
                            {group.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            ) : null,
        }),
        [positionMode, flowChoice, flowGroups]
    );
    // The clock header does not fit the one-line panel header next to the
    // legends (it wraps, and was cut to nothing there), so it gets a row of its
    // own: above the clock panel, or, with the clock on the main chart, above
    // the probability panel.
    const clockOverlay = clock?.overlay ?? null;
    const panelOverlays = useMemo(
        () => ({ clock: clockOverlay }),
        [clockOverlay]
    );
    const panelStrips = useMemo(
        () => ({
            probability: clockHeaderOnMain ? clockHeader : null,
            clock: clockPanelShown ? clockHeader : null,
        }),
        [clockHeader, clockHeaderOnMain, clockPanelShown]
    );

    const exportMarkets = useCallback(
        (outcomes: readonly Outcome[]): ExportMarket[] =>
            outcomes.flatMap(outcome => {
                const conditionId = marketIdOf(outcome);
                return conditionId
                    ? [{ conditionId, label: outcome.label }]
                    : [];
            }),
        []
    );
    const shownExportMarkets = useMemo(
        () => exportMarkets(chartOutcomes),
        [exportMarkets, chartOutcomes]
    );
    const allExportMarkets = useMemo(
        () => exportMarkets(event?.outcomes ?? []),
        [exportMarkets, event]
    );

    // The chart's panels whose data is on its way: drawn as placeholders at
    // their final size, so the card keeps its height when they land.
    const singleHasFlow =
        singleMarketId !== null &&
        (latest.byMarket.get(singleMarketId)?.size ?? 0) > 0;
    // The flow panel of the outcome shown: kept while its flow loads if the
    // latest flow says it has some, or has not answered yet (an outcome opened
    // with the page); the chart waits for that answer (at most FLOW_WAIT_MS),
    // so a slot that goes away goes before anything is drawn in the card.
    const latestPending = canFlow && !latest.settled;
    const flowPending =
        flowScope !== null && !flow.settled && (singleHasFlow || latestPending);
    const restOfPricesLate = useWaitOver(
        event ? marketIds.join(',') : null,
        !candlesLoading && candles.length > 0 && !candlesSettled,
        PRICES_WAIT_MS
    );
    const pricesPending =
        !event ||
        candlesLoading ||
        (!candlesSettled && !restOfPricesLate) ||
        waitingForFlowOrder ||
        (flowScope !== null && !latestKnown);
    const clockPending =
        researcher &&
        clock === null &&
        !clockFailed &&
        (!event || clockMarkets.length > 0 || waitingForFlowOrder);
    const pendingPanels = useMemo<PanelId[]>(() => {
        const list: PanelId[] = [];
        if (pricesPending) list.push('probability');
        if (clockPending) list.push('clock');
        if (flowPending) list.push('flow');
        return list;
    }, [pricesPending, clockPending, flowPending]);
    const pendingStripHeights = useMemo(
        () =>
            clockPending
                ? {
                      clock: compact
                          ? CLOCK_STRIP_HEIGHT.compact
                          : CLOCK_STRIP_HEIGHT.wide,
                  }
                : NO_STRIP_HEIGHTS,
        [clockPending, compact]
    );
    // Rows of the values strip above a phone's chart: one per outcome drawn
    // and, stacked, one for the rest.
    const valueRows = event
        ? series.filter(s => s.id.startsWith('prob:') && s.id !== 'prob:others')
              .length + (stacked ? 1 : 0)
        : DEFAULT_VALUE_ROWS;
    // The toolbar stays a placeholder until the chart has first filled, then
    // stays (a later load of other outcomes keeps it).
    const [filledFor, setFilledFor] = useState<string | null>(null);
    if (event && bounds && !pricesPending && filledFor !== event.id) {
        setFilledFor(event.id);
    }
    const toolbarReady = event !== null && filledFor === event.id;

    const totalVolume = useMemo(
        () =>
            event
                ? event.totalVolume ??
                  event.outcomes.reduce((sum, o) => sum + volumeOf(o), 0)
                : 0,
        [event]
    );

    return (
        <PageFrame>
            {eventError && (
                <Alert severity="error">This event could not be loaded.</Alert>
            )}

            <EventHeader
                event={event}
                loading={!eventError && (eventLoading || !event)}
                alertCount={alertsSettled ? alerts.length : null}
            />

            {researcher && event && !waitingForFlowOrder && (
                <ClockBoundary onFailed={onClockFailed}>
                    <Suspense fallback={null}>
                        <ClockBridge
                            key={event.id}
                            markets={clockMarkets}
                            window={fetchRange}
                            onOutput={setClock}
                            paused={clockAway && !clockOnScreenElsewhere}
                            closed={viewClosed}
                            clockOnMain={clockMain.on}
                            canMoveToMain={clockMain.possible || clockMain.on}
                            onClockOnMain={onClockOnMain}
                        />
                    </Suspense>
                </ClockBoundary>
            )}

            {single && (
                <OutcomeStrip
                    outcome={single}
                    color={colors.get(single.id) ?? null}
                    showBack={!selection.isSingleOutcomeEvent}
                    showFlow={showFlow}
                    flowPending={latestPending}
                    changePending={pricesPending}
                    flow={
                        singleMarketId
                            ? latest.byMarket.get(singleMarketId)
                            : undefined
                    }
                    onBack={selection.clearOutcomes}
                    change={singleChange}
                    rangeLabel={rangeLabel}
                />
            )}

            {!eventError && !event && (
                <SectionCard data-testid="event-chart-card" sx={{ p: 1.5 }}>
                    <ChartToolbarPlaceholder />
                    <ChartStackPlaceholder
                        panels={pendingPanels}
                        availableHeight={availableHeight}
                        stripHeights={pendingStripHeights}
                        valueRows={valueRows}
                    />
                </SectionCard>
            )}

            {bounds && (
                <SectionCard
                    data-testid="event-chart-card"
                    sx={
                        fullscreen
                            ? fullscreenCardSx(theme.zIndex.modal, fill)
                            : { p: 1.5, position: 'relative' }
                    }
                >
                    {toolbarReady && !pricesPending && !candlesAllLoaded && (
                        // New prices for a filled chart (another range): the
                        // lines stay until they land, a thin line says so.
                        <LinearProgress
                            data-testid="chart-refreshing"
                            aria-label="Loading prices"
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 2,
                            }}
                        />
                    )}
                    {!toolbarReady ? (
                        <ChartToolbarPlaceholder />
                    ) : (
                        <ChartToolbar
                            displayRange={range ?? bounds}
                            zoomed={range !== null}
                            quickRanges={quickRanges}
                            onQuickRange={onQuickRange}
                            mode={chartMode}
                            candlesAllowed={candlesAllowed}
                            stackedAllowed={stackedAllowed}
                            onModeChange={setChosenMode}
                            resolutionChoice={win.resolutionChoice}
                            resolutionLabel={win.resolutionLabel}
                            onResolutionChange={win.setResolutionChoice}
                            showVolume={showVolume}
                            onToggleVolume={() => setShowVolume(v => !v)}
                            exportSlot={
                                event && (
                                    <ExportBox
                                        key={event.id}
                                        eventId={event.id}
                                        eventSlug={event.slug ?? ''}
                                        eventTitle={event.title}
                                        shownMarkets={shownExportMarkets}
                                        allMarkets={allExportMarkets}
                                        bounds={bounds}
                                        shownRange={range}
                                        loadPage={loadCandlePage}
                                        isFatal={isOldFunction}
                                        clockExport={
                                            clock?.clockExport ?? undefined
                                        }
                                    />
                                )
                            }
                            fullscreen={fullscreen}
                            onToggleFullscreen={onToggleFullscreen}
                        >
                            {hiddenChips.map(chip => (
                                <Chip
                                    key={chip.id}
                                    icon={<VisibilityIcon />}
                                    label={chip.title}
                                    size="small"
                                    variant="outlined"
                                    aria-label={`Show the panel: ${chip.title}`}
                                    onClick={() => showPanel(chip.id)}
                                    sx={{
                                        height: 22,
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        '& .MuiChip-icon': { fontSize: '14px' },
                                    }}
                                />
                            ))}
                        </ChartToolbar>
                    )}
                    <ChartStack
                        series={chartSeries}
                        moves={moves}
                        onMovesChange={handleMoves}
                        bounds={bounds}
                        range={range}
                        onRangeChange={setRange}
                        availableHeight={availableHeight}
                        minMainHeight={fullscreen ? fit.floor : undefined}
                        fill={fullscreen && fill}
                        foldPanels={foldPanels}
                        onFolded={onFolded}
                        loading={eventLoading || pricesPending}
                        pendingPanels={pendingPanels}
                        pendingStripHeights={pendingStripHeights}
                        pendingValueRows={
                            pricesPending || !candlesAllLoaded ? valueRows : 0
                        }
                        hiddenPanels={hiddenPanels}
                        onHidePanel={hidePanel}
                        panelExtras={panelExtras}
                        panelStrips={panelStrips}
                        panelOverlays={panelOverlays}
                        lifetime={lifetime}
                        rangeLabel={rangeLabel}
                    />
                </SectionCard>
            )}

            {/* The clock's diagnostics card: not part of the chart card, so
                full screen stays the chart only. It brings its own error
                boundary; this one covers a render error that gets past it. */}
            {bounds && clock?.section && (
                <ClockBoundary>{clock.section}</ClockBoundary>
            )}

            {event && single && singleMarketId && canWallets && (
                <SectionCard data-testid="event-wallets-card" sx={{ p: 1.5 }}>
                    <WalletsPanel
                        marketId={singleMarketId}
                        selectedWallets={wallets}
                        colors={walletColorMap}
                        tradesByWallet={walletTrades.tradesByWallet}
                        loadingWallets={walletTrades.loadingWallets}
                        failedWallets={failedWallets}
                        onRetryTrades={retryWallet}
                        tradeTypes={tradeTypes}
                        onTradeTypesChange={setTradeTypes}
                        onToggleWallet={selection.toggleWallet}
                        onClearWallets={selection.clearWallets}
                    />
                </SectionCard>
            )}

            {!eventError && (!event || !selection.isSingleOutcomeEvent) && (
                <SectionCard
                    data-testid="event-outcomes-card"
                    aria-busy={!listReady}
                    sx={{ p: 1.5 }}
                >
                    <OutcomeList
                        loading={!listReady}
                        shown={split.shown}
                        hidden={split.hidden}
                        selectedIds={selection.selectedIds}
                        colors={chartColors}
                        totalVolume={totalVolume}
                        clickable
                        showFlow={showFlow}
                        latestFlow={latest.byMarket}
                        sort={effectiveSort}
                        onSortChange={setSort}
                        onToggle={selection.toggleOutcome}
                        onSelectAll={selection.selectAllOutcomes}
                        onClear={selection.clearOutcomes}
                    />
                </SectionCard>
            )}
        </PageFrame>
    );
}
